import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import LiveRange from '@ckeditor/ckeditor5-engine/src/model/liverange';
import { CkeditorConstants } from '../../src/utils';

const {
  helperAttributes: { TRACK_CHANGES_EDITING, NUMBERED_CITATION }
} = CkeditorConstants;

export default class TrackChangesSeparateSuggestions extends Plugin {
  init() {
    const editor = this.editor;
    this.trackChangeSeperateSuggestions( editor );
  }

  trackChangeSeperateSuggestions( editor ) {
    const editingView = editor.editing.view;
    // Suggestion separation is implemented using suggestion attributes mechanism.
    // Track changes prevents joining suggestions which have different attributes.
    // We can store specific IDs with suggestions to decide which suggestions can be joined together, and which should be separated
    //
    // The first requirement is to never join suggestions done in different sessions.
    // To implement that, all suggestions created during one session should have the same session ID attribute.
    // The session ID should be pseudo-random, so that all suggestions created during different session have different ID.
    this._sessionId = new Date().getTime();
    // The second requirement is to never join suggestions that were created as separate suggestions.
    // The third requirement is to never join suggestions that were created in different users actions. The exception here is typin
    // where we want to grow the suggestion as the user types (instead of having each character in a separate suggestion).
    //
    // `_actionCounter` is used to generate unique IDs for suggestions created during one session.
    // We want to allow joining suggestions that were created during one user action (e.g. one paste).
    // We want to keep seperated suggestions that were created during different user actions.
    // All suggestions created during the same user action will have the same action counter.
    //
    // `_sessionId` and `_actionCounter` are joined to generate a unique suggestion ID, saved with in the suggestion attributes.
    this._actionCounter = 0;
    // Stores a name of currently processed user action. Can be `'typing'`, `'enter'` or `null` (for all other actions).
    // Used to recognize that the user is typing and to perform a different logic in this case.
    this._currentAction = null;
    // Stores `uniqueId` of suggestion that are typing suggestions created during this editing session.
    // New typing suggestions can be joined to these suggestions (to prevent storing each character in a separate suggestion).
    this._typingSuggestionsIds = new Set();
    // Counter used to generate a globally unique id for each numbered-citation suggestion,
    // so that every numbered-citation span is tracked as an individual suggestion.
    this._citationCounter = 0;
    // Guard flag set while re-issuing split mark calls to avoid re-entrant splitting.
    this._isSplitting = false;
    // `_afterChanges` is an internal event fired after all change (and enqueueChange) callbacks are fired.
    // The editor uses it to start rendering, as this is the moment after all changes related to one user action are applied.
    // There is an issue to make this event public: https://github.com/ckeditor/ckeditor5/issues/14875.
    // When the issue is closed, below callback will have to be updated to use the public event.
    editor.model.on( '_afterChanges', () => {
      // Since action counter is updated after each user action, all changes done during one specific action may be joined.
      // At the same time, suggestions created during different users actions will never be joined.
      // The exception here is typing, for which we will have a separate handling.
      this._actionCounter++;
      // Reset current action.
      this._currentAction = null;
    } );
    // Now, we need to make `TrackChangesEditing` plugin methods decorated, so we are able to listen to them being called
    // and pass IDs to newly created attribute suggestions.
    const trackChanges = editor.plugins.get( TRACK_CHANGES_EDITING );
    // The list of `TrackChangesEditing` methods that create suggestions.
    // The keys are indexes on which the attributes parameter is passed to a given method. See API docs for these methods to learn
    // NOTE: this list must be updated when `TrackChangesEditing` API changes (e.g. a method API changes or a new method is added)
    const methods = {
      markInsertion: 2,
      markMultiRangeInsertion: 2,
      markDeletion: 2,
      markMultiRangeDeletion: 2,
      markBlockFormat: 4,
      markMultiRangeBlockFormat: 4,
      markInlineFormat: 3
    };
    for ( const [ methodName, attributesIndex ] of Object.entries( methods ) ) {
      trackChanges.decorate( methodName );
      // Listener with high priority will be fired *before* the actual method code.
      // The callback can modify the parameters with which the method was called.
      // We will use it to add suggestion unique ID to attributes.
      this.listenTo(
        trackChanges,
        methodName,
        ( evt, args ) => this._addIdAttribute( evt, args, attributesIndex, methodName ),
        { priority: 'high' }
      );
    }
    // Reference to the track changes editing plugin, used to re-issue mark calls when splitting citations.
    this._trackChanges = trackChanges;
    // React to "known" user actions such as: typing, deleting, enter and paste.
    // Set current action to a proper value. It will be handled when one of `TrackChangesEditing` methods in called soon after.
    editor.commands.get( 'insertText' ).on( 'execute', () => this._setCurrentAction( 'typing' ), { priority: 'highest' } );

    editor.commands.get( 'delete' ).on( 'execute', () => this._setCurrentAction( 'delete' ), { priority: 'highest' } );
    editor.commands.get( 'deleteForward' ).on( 'execute', () => this._setCurrentAction( 'delete' ), { priority: 'highest' } );
    editor.commands.get( 'enter' ).on( 'execute', () => this._setCurrentAction( 'enter' ), { priority: 'highest' } );

    // NOTE: UNCOMMENT TO DISABLE GENERAL HANDLING AND ENABLE PASTE INTEGRATION:
    editingView.document.on( 'clipboardInput', () => this._setCurrentAction( 'paste' ), { priority: 'highest' } );
  }

  _setCurrentAction( action ) { this._currentAction = action; }

  _addIdAttribute( evt, args, index, methodName ) {
    // Check if attributes parameter is set in this `markXXX...` method call. If not, set it to an empty object.
    if ( args[ index ] === undefined ) {
      args[ index ] = {};
    }

    // When we re-issue split mark calls (see `_markCitationsSeparately`), the attributes
    // are already prepared, so we let the original method run untouched.
    if ( this._isSplitting ) {
      return;
    }
    // A pasted (or otherwise inserted) fragment can contain mixed content: text, images,
    // numbered-citation spans, etc. Track changes marks the whole range as a single
    // suggestion, so to track each numbered-citation span individually we split the mark
    // call into separate sub-range calls: each citation gets its own unique id, and the
    // surrounding content keeps the shared id.
    if ( ( methodName === 'markInsertion' || methodName === 'markDeletion' ) &&
      this._markCitationsSeparately( args, index, methodName ) ) {
      // The range was handled by re-issuing split mark calls; stop the original call so the
      // whole range is not marked as a single suggestion.
      evt.stop();
      return;
    }
    switch ( this._currentAction ) {
    case 'typing':
      this._addIdAttributeTyping( args, index, 'insertion' );
      break;
    case 'delete':
      this._addIdAttributeTyping( args, index, 'deletion' );
      break;
    case 'enter':
      this._addIdAttributeEnter( args, index );
      break;
      // NOTE: UNCOMMENT TO DISABLE GENERAL HANDLING AND ENABLE PASTE INTEGRATION:
      // NOTE: REMOVE THIS LINE TO DISABLE GENERAL HANDLING AND ENABLE PASTE INTEGRATION.
    case 'paste':
      this._addIdAttributePaste( args, index );
      break;
    default:
      this._addIdAttributeDefault( args, index );
      break;
    }
  }
  _addIdAttributeDefault( args, index ) {
    // Add unique IDs for this suggestion.
    args[ index ].uniqueId = this._sessionId + ':' + this._actionCounter;
  }

  // Collects the numbered-citation elements contained in the given model range, in order.
  _collectCitations( range ) {
    const citations = [];
    if ( !range || typeof range.getWalker !== 'function' ) {
      return citations;
    }
    for ( const { item } of range.getWalker() ) {
      if ( item.name === NUMBERED_CITATION ) {
        citations.push( item );
      }
    }
    return citations;
  }

  // Returns a stable, unique id for a single numbered-citation element. The citation's own
  // `id`/`about` attribute is used so the same citation always maps to the same suggestion;
  // a session counter is used as a fallback to guarantee uniqueness.
  _citationUniqueId( citation ) {
    const id = citation.getAttribute( 'id' ) ||
      citation.getAttribute( 'about' ) ||
      ( 'seq:' + ( this._citationCounter++ ) );
    return this._sessionId + ':citation:' + id;
  }

  // Splits a single insertion/deletion mark call into separate sub-range calls so that each
  // numbered-citation span becomes its own suggestion, while the surrounding content keeps
  // the shared id. Returns `true` when the range contained citations and was handled here.
  _markCitationsSeparately( args, index, methodName ) {
    const range = args[ 0 ];
    const citations = this._collectCitations( range );
    if ( !citations.length ) {
      return false;
    }
    const model = this.editor.model;
    const baseUniqueId = this._sessionId + ':' + this._actionCounter;
    const segments = [];
    let cursor = range.start;
    for ( const citation of citations ) {
      const before = model.createPositionBefore( citation );
      const after = model.createPositionAfter( citation );
      // Surrounding content before this citation keeps the shared id.
      if ( cursor.isBefore( before ) ) {
        segments.push( { start: cursor, end: before, uniqueId: baseUniqueId } );
      }
      // The citation itself becomes its own suggestion.
      segments.push( { start: before, end: after, uniqueId: this._citationUniqueId( citation ) } );
      cursor = after;
    }
    // Remaining surrounding content after the last citation keeps the shared id.
    if ( cursor.isBefore( range.end ) ) {
      segments.push( { start: cursor, end: range.end, uniqueId: baseUniqueId } );
    }
    // Re-issue the mark call for each segment. `_isSplitting` prevents re-entrant splitting.
    const liveRanges = [];
    this._isSplitting = true;
    try {
      // Deleting an insertion removes content immediately; track moving positions and process the last segment first.
      const pendingSegments = methodName === 'markDeletion' ?
        segments.map( segment => {
          const liveRange = LiveRange.fromRange( model.createRange( segment.start, segment.end ) );
          liveRanges.push( liveRange );
          return { ...segment, liveRange };
        } ).reverse() :
        segments;
      // Cancelling inserted content mutates the model; live ranges keep the remaining segments valid.
      for ( const segment of pendingSegments ) {
        const segmentArgs = args.slice();
        const currentRange = segment.liveRange || segment;
        segmentArgs[ 0 ] = model.createRange( currentRange.start, currentRange.end );
        // Earlier deletions may already have removed this segment's content.
        if ( segmentArgs[ 0 ].isCollapsed ) {
          continue;
        }
        // Keep the caller's suggestion metadata while assigning this segment its separate identity.
        segmentArgs[ index ] = { ...args[ index ], uniqueId: segment.uniqueId };
        this._trackChanges[ methodName ]( ...segmentArgs );
      }
    } finally {
      // Release model listeners and the re-entry guard even when tracking throws.
      liveRanges.forEach( liveRange => liveRange.detach() );
      this._isSplitting = false;
    }
    return true;
  }

  _addIdAttributeTyping( args, index, type ) {
    // Check if the new proposed typing suggestion is next to or inside another typing suggestion created in this session.
    // If so, allow these suggestions to be joined -- use `uniqueId` of the existing suggestion.
    const range = args[ 0 ];
    // Get all existing suggestions. Skip all removed suggestions.
    // Leave only typing suggestions of the same type (insertion/deletion) which were created during this session.
    const suggestions = this.editor.plugins.get( 'TrackChangesEditing' ).getSuggestions( { skipNotAttached: true } );
    const typingSuggestions = suggestions.filter( suggestion => {
      return suggestion.type == type && this._typingSuggestionsIds.has( suggestion.attributes.uniqueId );
    } );
    let uniqueId = this._sessionId + ':' + this._actionCounter;

    for ( const suggestion of typingSuggestions ) {
      const suggestionRange = suggestion.getFirstRange();
      // Typing suggestion can have only one range.
      const isNextTo = range.isIntersecting( suggestionRange ) ||
        range.start.isEqual( suggestionRange.end ) ||
        range.end.isEqual( suggestionRange.start );
      if ( isNextTo ) {
        uniqueId = suggestion.attributes.uniqueId;
        break;
      }
    }
    args[ index ].uniqueId = uniqueId;
    this._typingSuggestionsIds.add( uniqueId );
  }

  // This method remains separate from the previous one to make future improvements easier.
  _addIdAttributeEnter( args, index ) {
    // Enter support is a bit tricky and we would recommend to treat it as regular typing (so it joins with other typings).
    // However, as promised, we tried to experiment whether the logic requested by you, or similar, can be implemented.
    // We concluded that enter works similar to typing, with small difference:
    // If enter happens before or in the middle of a typing suggestion, treat it the same way as typing.
    // If enter happens after typing suggestion, do not join them (force new suggestion). However, still add the enter suggestion a
    // typing suggestion, so that following typing suggestions can be joined to it.
    const range = args[ 0 ];

    // Get all existing suggestions. Skip all removed suggestions.
    // Leave only typing insertion suggestions which were created during this session.
    const suggestions = this.editor.plugins.get( 'TrackChangesEditing' ).getSuggestions( { skipNotAttached: true } );
    const typingSuggestions = suggestions.filter( suggestion => {
      return this._typingSuggestionsIds.has( suggestion.attributes.uniqueId );
    } );
    let uniqueId = this._sessionId + ':' + this._actionCounter;
    for ( const suggestion of typingSuggestions ) {
      const suggestionRange = suggestion.getFirstRange();
      // Typing suggestion can have only one range.
      if ( range.isIntersecting( suggestionRange ) || range.end.isEqual( suggestionRange.start ) ) {
        uniqueId = suggestion.attributes.uniqueId;
        break;
      }
    }
    args[ index ].uniqueId = uniqueId;
    this._typingSuggestionsIds.add( uniqueId );
  }

  _addIdAttributePaste( args, index ) {
    if ( args[ 2 ] === undefined ) {
      // `args[ 2 ]` is the third parameter of `markInsertion()` call, which is the suggestion attributes.
      // If it is not set, set it to an empty object (the default value).
      args[ 2 ] = {};
    }
    // Add `actionId` attribute and set it to a unique value.//
    // This way a suggestion created when pasting will never be joined,
    // with any other suggestion.
    args[ 2 ].actionId = this._sessionId;
    // Add unique IDs for this suggestion.
    args[ index ].uniqueId = this._sessionId + ':' + this._actionCounter;
  }
}
